import { ref } from 'vue'
import { defineStore } from 'pinia'
import { CompleteMultipartUploadCommand, UploadPartCommand } from '@aws-sdk/client-s3'
import { useS3Store } from '@/store/S3.store'
import { processStatuses } from '@/configs/dataTypes.config'

export const useTaskQueueStore = defineStore('useTaskQueueStore', {
  state: () => {
    const S3Store = useS3Store()

    const tasks = ref([])
    const queue = ref([])

    const running = ref(0)
    const cuncurrency = ref(3)
    const processing = ref({})

    const enqueue = async (task) => {
      queue.value.push(task)

      processing.value[task.id] = {
        status: processStatuses.processing,
        progress: 0,
        message: processStatuses.processing
      }
      await process()
    }

    const process = async () => {
      if (running.value >= cuncurrency.value || queue.value.length === 0) {
        return
      }

      const task = queue.value[0]

      if (
        processing.value[task.id].status === processStatuses.completed ||
        processing.value[task.id].status === processStatuses.finalize
      ) {
        processing.value[task.id].message = 'Уже' + processing.value[task.id].status

        return
      }

      const partBuffers = Object.keys(task.partBuffers) || []

      if (partBuffers.length === 0 && task.totalParts === task.parts.length) {
        const { bucket, key, id, parts } = queue.value.shift()

        await completeMultipartUploadTask(bucket, key, id, parts)
        return
      }

      if (partBuffers.length > 0) {
        const { bucket, key, id } = task

        const partNumber = partBuffers[0]
        if (!partNumber) return

        const buffer = task.partBuffers[partNumber]
        if (!buffer) return

        delete task.partBuffers[partNumber]

        await sendPart(bucket, key, id, partNumber, buffer)
      }
    }

    async function completeMultipartUploadTask (bucket, key, id, parts) {
      if (
        !parts || parts.length < 0 ||
        processing.value[id].status === processStatuses.completed ||
        processing.value[id].status === processStatuses.error ||
        processing.value[id].status === processStatuses.finalize) {
        return
      }

      cuncurrency.value = 1
      running.value++
      processing.value[id].status = processStatuses.finalize

      try {
        await S3Store.s3Client.send(
          new CompleteMultipartUploadCommand({
            Bucket: bucket,
            Key: key,
            UploadId: id,
            MultipartUpload: {
              Parts: parts.sort((a, b) => a.PartNumber - b.PartNumber)
            }
          })
        )

        queue.value = queue.value.filter(i => i.id !== id)
        processing.value[id].status = processStatuses.completed
      } catch (err) {
        queue.value = queue.value.filter(i => i.id !== id)
        processing.value[id].message = err.$response
        processing.value[id].status = processStatuses.error
      } finally {
        cuncurrency.value = 3
        running.value--

        await process()
      }
    }

    async function sendPart (bucket, key, id, partNumber, uint8Array) {
      if (
        !partNumber || processing.value[id].status === processStatuses.finalize ||
        processing.value[id].status === processStatuses.error ||
        processing.value[id].status === processStatuses.completed
      ) {
        return
      }

      const uploadCmd = new UploadPartCommand({
        Bucket: bucket,
        Key: key,
        PartNumber: partNumber,
        UploadId: id,
        Body: uint8Array
      })

      running.value++

      try {
        const req = await S3Store.s3Client.send(uploadCmd)

        const task = await queue.value.find(i => i.id === id)
        if (task) {
          task.parts.push({
            ETag: req.ETag,
            PartNumber: partNumber
          })

          processing.value[id] = {
            status: processing.value[id].status,
            progress: task.parts.length > 0 ? Math.round(task.parts.length / task.totalParts * 100) : 0
          }
        }
      } catch (err) {
        processing.value[id].message = err.$response
        processing.value[id].status = processStatuses.error
      } finally {
        running.value--
        await process()
      }
    }

    return {
      tasks,
      queue,
      processing,
      enqueue
    }
  }
})
